import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import { Tooltip } from '@mui/material'
import { ReactNode, useEffect, useMemo, useState } from 'react'

interface TableCompProps {
  data: Record<string, any>[]
  columns: {
    title: string
    titleIcon?: JSX.Element
    field: string
    hidden?: boolean
    render?: (data: Record<string, any>) => ReactNode
    sortable?: boolean
    width?: string
  }[]
  customColumnClassName?: (data: Record<string, any>) => string
}

export default function TableComp(props: TableCompProps) {
  const { data, columns: propsColumns, customColumnClassName } = props
  const pageSize = 15
  const totalPages = Math.ceil(data.length / pageSize)
  const [page, setPage] = useState(0)
  const [sortField, setSortField] = useState<string>('Brand Name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const columns = useMemo(() => {
    return propsColumns.filter((item) => !item.hidden)
  }, [propsColumns])

  // Apply anti-scraping protections
  useEffect(() => {
    // Disable right-click menu
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault()
      return false
    }

    // Disable keyboard shortcuts commonly used for copying
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent Ctrl+C, Ctrl+S, Ctrl+P, etc.
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'c' || e.key === 's' || e.key === 'p' || e.key === 'a')
      ) {
        e.preventDefault()
        return false
      }
    }

    // Add hidden characters to obfuscate text content
    const obfuscateTextContent = () => {
      const tableElements = document.querySelectorAll('.anti-copy-text')
      tableElements.forEach((el) => {
        const text = el.textContent || ''
        if (text && !el.getAttribute('data-obfuscated')) {
          const obfuscated = Array.from(text)
            .map(
              (char) => char + '\u200D' // Add zero-width joiner
            )
            .join('')
          el.textContent = obfuscated
          el.setAttribute('data-obfuscated', 'true')
        }
      })
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)

    // Run obfuscation after render
    setTimeout(obfuscateTextContent, 500)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [page, sortField, sortDirection])

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle sort direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new sort field and default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
    // Reset to first page when sorting
    setPage(0)
  }

  const sortedData = useMemo(() => {
    // Create a copy of data to avoid mutating props
    const dataToSort = [...data]

    return dataToSort.sort((a, b) => {
      let aValue = a[sortField]
      let bValue = b[sortField]

      // Handle range values like "5-25", ">500", "100-500" for numeric comparisons
      const rangeRegex = /^(\d+|>)(-)?(\d+)?$/
      const aIsRange = typeof aValue === 'string' && rangeRegex.test(aValue)
      const bIsRange = typeof bValue === 'string' && rangeRegex.test(bValue)

      if (aIsRange && bIsRange) {
        // Extract the first number from each range for sorting
        const aMatch = aValue.match(/^(\d+|>)/)
        const bMatch = bValue.match(/^(\d+|>)/)

        if (aMatch && bMatch) {
          const aNum =
            aMatch[0] === '>'
              ? Number.MAX_SAFE_INTEGER
              : parseInt(aMatch[0], 10)
          const bNum =
            bMatch[0] === '>'
              ? Number.MAX_SAFE_INTEGER
              : parseInt(bMatch[0], 10)

          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum
        }
      }

      // Handle price ranges like "$12.5-$15"
      if (sortField === 'Avg Price Range') {
        const extractPrice = (val: string) => {
          const match = val.match(/\$(\d+\.?\d*)/)
          return match ? parseFloat(match[1]) : 0
        }

        const aPrice = extractPrice(aValue)
        const bPrice = extractPrice(bValue)

        return sortDirection === 'asc' ? aPrice - bPrice : bPrice - aPrice
      }

      // Handle "No Presence" special case
      if (aValue === 'No Presence')
        aValue = sortDirection === 'asc' ? '\uffff' : ''
      if (bValue === 'No Presence')
        bValue = sortDirection === 'asc' ? '\uffff' : ''

      // Default string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      // Fallback comparison
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [data, sortField, sortDirection])

  const onNext = () => {
    if (page < totalPages - 1) setPage(page + 1)
  }

  const onPrev = () => {
    if (page > 0) setPage(page - 1)
  }

  // Define default column widths based on field type
  const getColumnWidth = (field: string): string => {
    switch (field) {
      case 'Brand Name':
        return '280px'
      case 'Locations Bucket':
        return '150px'
      case 'Avg Rating Range':
      case 'Google Play Store Rating Range':
      case 'Apple Store Rating Range':
      case 'Rating per Store Range':
        return '120px'
      case 'Reviews per Store Range':
      case 'Avg Price Range':
      case 'Google Play Store #Ratings Range':
      case 'Apple Store #Ratings Range':
        return '150px'
      case 'FB followers Range':
      case 'Insta followers range':
      case 'Twitter followers range':
      case 'LinkedIn followers range':
        return '160px'
      default:
        return '120px'
    }
  }

  return (
    <>
      <div>
        <div className="w-full overflow-y-hidden h-full overflow-x-auto">
          {/* Anti-copy styles applied to make scraping harder */}
          <div
            className="table-container anti-copy"
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              position: 'relative',
              minWidth: '100%'
            }}>
            <table
              className="w-full text-sm text-left rtl:text-right text-gray-500 border-separate border-spacing-0"
              style={{ width: '100%' }}>
              <thead className="text-xs text-[#374151] uppercase bg-[#F9FAFB]">
                <tr>
                  {columns.map((item, i) => {
                    const isSorted = sortField === item.field
                    const sortable = item.sortable !== false // Default to true if not specified
                    const columnWidth = item.width || getColumnWidth(item.field)

                    return (
                      <th
                        key={`${item.title}-${i}`}
                        className={`px-2 py-3 ${
                          sortable ? 'cursor-pointer hover:bg-gray-200' : ''
                        }`}
                        style={{
                          minWidth: '100px',
                          maxWidth: item.width || getColumnWidth(item.field),
                          height: '4rem' // Taller header to accommodate two lines
                        }}
                        onClick={
                          sortable ? () => handleSort(item.field) : undefined
                        }>
                        <div className="flex flex-row items-start justify-between h-full">
                          <div className="flex items-start mr-1 overflow-hidden">
                            {item.titleIcon && (
                              <div className="h-4 flex-shrink-0 mr-1 mt-1">
                                {item.titleIcon}
                              </div>
                            )}
                            <Tooltip
                              title={sortable ? 'Click to sort' : ''}
                              arrow
                              placement="top">
                              <span
                                className={`uppercase text-xs font-medium overflow-hidden min-h-[2.5rem] anti-copy-text ${
                                  sortable
                                    ? 'border-b border-dashed border-gray-400'
                                    : ''
                                }`}
                                style={{
                                  fontSize: 'inherit',
                                  color: 'inherit',
                                  lineHeight: '1.2rem',
                                  maxWidth: '90%',
                                  display: 'block',
                                  wordBreak: 'break-word',
                                  hyphens: 'auto',
                                  whiteSpace: 'normal'
                                }}>
                                {item.title}
                              </span>
                            </Tooltip>
                          </div>
                          {sortable && (
                            <div className="flex-shrink-0 ml-1 w-5">
                              {isSorted && sortDirection === 'asc' ? (
                                <ArrowDropUpIcon style={{ fontSize: '18px' }} />
                              ) : isSorted && sortDirection === 'desc' ? (
                                <ArrowDropDownIcon
                                  style={{ fontSize: '18px' }}
                                />
                              ) : (
                                <div className="flex flex-col opacity-30">
                                  <ArrowDropUpIcon
                                    style={{
                                      fontSize: '18px',
                                      marginBottom: '-8px'
                                    }}
                                  />
                                  <ArrowDropDownIcon
                                    style={{ fontSize: '18px' }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedData
                  .slice(page * pageSize, (page + 1) * pageSize)
                  .map((data, index) => (
                    <tr
                      key={`row-${index}`}
                      className={`bg-white hover:bg-slate-100 transition-all border-b ${
                        customColumnClassName?.call({}, data) || ''
                      }`}>
                      {columns.map((item, j) => {
                        const val =
                          typeof item.render === 'function'
                            ? item.render(data)
                            : data[item.field]

                        const columnWidth =
                          item.width || getColumnWidth(item.field)

                        return (
                          <td
                            key={`row-${item.title}-${j}`}
                            className="px-2 py-4"
                            style={{
                              minWidth: '100px',
                              maxWidth: item.width || getColumnWidth(item.field)
                            }}>
                            {typeof val === 'string' ? (
                              val === 'No Presence' ? (
                                <p className="text-gray-400 text-sm font-normal overflow-hidden anti-copy-text whitespace-nowrap">
                                  No Presence
                                </p>
                              ) : (
                                <p
                                  className="text-black text-base overflow-hidden anti-copy-text"
                                  style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    textOverflow: 'ellipsis',
                                    wordBreak: 'break-word'
                                  }}>
                                  {val}
                                </p>
                              )
                            ) : (
                              <div className="anti-copy-content">{val}</div>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>

            {/* Invisible content to disrupt scrapers */}
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-0 pointer-events-none">
              {Array.from({ length: 50 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`
                  }}>
                  {`data-${Math.random().toString(36).substring(2)}`}
                </div>
              ))}
            </div>

            {/* CSS grid overlay (invisible) to confuse scrapers */}
            <div
              className="absolute inset-0 pointer-events-none opacity-0"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(45deg, rgba(0,0,0,0.01), rgba(0,0,0,0.01) 10px, rgba(0,0,0,0) 10px, rgba(0,0,0,0) 20px)',
                zIndex: 10
              }}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between px-3 py-4 gap-3 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing {page * pageSize + 1}-
            {Math.min((page + 1) * pageSize, data.length)} of {data.length}{' '}
            results
          </div>

          <nav className="flex items-center justify-center">
            <ul className="flex items-center flex-wrap space-x-1 text-sm">
              {/* First page button */}
              <li>
                <button
                  onClick={() => setPage(0)}
                  disabled={page === 0}
                  className={`flex items-center justify-center w-9 h-9 rounded-md border ${
                    page === 0
                      ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                      : 'text-gray-500 border-gray-300 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                  aria-label="First page">
                  <span className="sr-only">First page</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </li>

              {/* Previous page button */}
              <li>
                <button
                  onClick={onPrev}
                  disabled={page === 0}
                  className={`flex items-center justify-center w-9 h-9 rounded-md border ${
                    page === 0
                      ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                      : 'text-gray-500 border-gray-300 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                  aria-label="Previous page">
                  <span className="sr-only">Previous</span>
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 6 10">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 1 1 5l4 4"
                    />
                  </svg>
                </button>
              </li>

              {/* Page numbers */}
              {(() => {
                const pageNumbers = []
                const maxVisiblePages = 5
                const showEllipsisStart = page > maxVisiblePages - 3
                const showEllipsisEnd =
                  page < totalPages - (maxVisiblePages - 2)

                if (totalPages <= maxVisiblePages) {
                  // Show all pages if not too many
                  for (let i = 0; i < totalPages; i++) {
                    pageNumbers.push(i)
                  }
                } else {
                  // Show limited pages with ellipsis
                  if (!showEllipsisStart) {
                    // Near start: show first maxVisiblePages-1 pages + last page
                    for (let i = 0; i < maxVisiblePages - 1; i++) {
                      pageNumbers.push(i)
                    }
                    if (showEllipsisEnd) {
                      pageNumbers.push('ellipsis-end')
                    }
                    pageNumbers.push(totalPages - 1)
                  } else if (!showEllipsisEnd) {
                    // Near end: show first page + last maxVisiblePages-1 pages
                    pageNumbers.push(0)
                    if (showEllipsisStart) {
                      pageNumbers.push('ellipsis-start')
                    }
                    for (
                      let i = totalPages - (maxVisiblePages - 1);
                      i < totalPages;
                      i++
                    ) {
                      pageNumbers.push(i)
                    }
                  } else {
                    // Middle: show first page + current area + last page
                    pageNumbers.push(0)
                    pageNumbers.push('ellipsis-start')

                    const startPage = Math.max(page - 1, 1)
                    const endPage = Math.min(page + 1, totalPages - 2)

                    for (let i = startPage; i <= endPage; i++) {
                      pageNumbers.push(i)
                    }

                    pageNumbers.push('ellipsis-end')
                    pageNumbers.push(totalPages - 1)
                  }
                }

                // Render page buttons
                return pageNumbers.map((pageNum, index) => {
                  if (
                    pageNum === 'ellipsis-start' ||
                    pageNum === 'ellipsis-end'
                  ) {
                    return (
                      <li key={`ellipsis-${index}`}>
                        <span className="flex items-center justify-center w-9 h-9 text-gray-500">
                          &#8230;
                        </span>
                      </li>
                    )
                  }

                  return (
                    <li key={index}>
                      <button
                        className={`flex items-center justify-center w-9 h-9 rounded-md border ${
                          pageNum === page
                            ? 'bg-blue-50 text-blue-600 border-blue-300 font-medium'
                            : 'text-gray-500 border-gray-300 hover:bg-gray-100 hover:text-gray-700'
                        }`}
                        onClick={() => setPage(pageNum as number)}>
                        {(pageNum as number) + 1}
                      </button>
                    </li>
                  )
                })
              })()}

              {/* Next page button */}
              <li>
                <button
                  onClick={onNext}
                  disabled={page >= totalPages - 1}
                  className={`flex items-center justify-center w-9 h-9 rounded-md border ${
                    page >= totalPages - 1
                      ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                      : 'text-gray-500 border-gray-300 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                  aria-label="Next page">
                  <span className="sr-only">Next</span>
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 6 10">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 9 4-4-4-4"
                    />
                  </svg>
                </button>
              </li>

              {/* Last page button */}
              <li>
                <button
                  onClick={() => setPage(totalPages - 1)}
                  disabled={page >= totalPages - 1}
                  className={`flex items-center justify-center w-9 h-9 rounded-md border ${
                    page >= totalPages - 1
                      ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                      : 'text-gray-500 border-gray-300 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                  aria-label="Last page">
                  <span className="sr-only">Last page</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                    <path
                      fillRule="evenodd"
                      d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </>
  )
}
